import { clsx, type ClassValue } from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';

export function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [breakpoint]);
  return isMobile;
}

/**
 * Validates that a string is a valid CSS class name
 * Prevents color values (rgba, rgb, hex) from being used as class names
 */
function isValidClassName(value: string): boolean {
  if (!value || typeof value !== 'string') return false;

  // Reject color values
  if (
    value.startsWith('#') ||
    value.startsWith('rgb') ||
    value.startsWith('rgba') ||
    value.startsWith('hsl') ||
    value.includes('rgba(') ||
    value.includes('rgb(')
  ) {
    return false;
  }

  // Reject values with spaces (except for valid Tailwind classes with spaces)
  // But reject if it looks like a color value
  if (
    value.includes('(') &&
    (value.includes('rgb') || value.includes('rgba'))
  ) {
    return false;
  }

  return true;
}

/**
 * Safely filters out invalid class names (like color values)
 */
function filterValidClasses(...inputs: ClassValue[]): ClassValue[] {
  return inputs.filter((input) => {
    if (typeof input === 'string') {
      // Split by spaces and filter out invalid parts
      const parts = input.split(/\s+/);
      const validParts = parts.filter((part) => {
        if (!part) return false;
        return isValidClassName(part);
      });
      return validParts.length > 0;
    }
    return true;
  });
}

export function cn(...inputs: ClassValue[]): string {
  // Filter out any invalid class names (like color values)
  const validInputs = filterValidClasses(...inputs);
  return twMerge(clsx(validInputs));
}
